import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SeriesMarker, UTCTimestamp } from 'lightweight-charts';
import { useApi, useServerOffset } from '@adiox/ui';
import { useStream } from '../stream/stream';
import { useActiveLogin } from '../trading/useTrading';
import type { Bar } from './chartModel';
import { tradeMarkers, type DealLike } from './tradeMarkers';
import { tradeLinks, type TradeLink } from './tradeLinks';

const NONE: SeriesMarker<UTCTimestamp>[] = [];
const NO_LINKS: TradeLink[] = [];

export interface TradeOverlay {
  markers: SeriesMarker<UTCTimestamp>[];
  links: TradeLink[];
}

/**
 * Entry and exit arrows, and the lines joining each entry to its exit, for the account's deals on this symbol, over the history the chart holds. The deals are read again
 * when a new deal arrives and when older bars are loaded.
 */
export function useTradeMarkers(
  symbol: string,
  bars: Bar[],
  colors: { up: string; down: string },
  enabled: boolean,
): TradeOverlay {
  const api = useApi();
  const login = useActiveLogin();
  const offset = useServerOffset() ?? 0;
  const lastDeal = useStream(
    (s) => s.recentEvents.findLast((e) => e.event.type === 'deal' && e.event.login === login)?.seq ?? 0,
  );
  const first = bars[0]?.t;
  // Bar times are server-clock seconds; the history filter takes UTC.
  const from = first === undefined ? undefined : new Date((first - offset * 60) * 1000).toISOString();

  const deals = useQuery({
    queryKey: ['chart-deals', login, from, lastDeal],
    enabled: enabled && login !== undefined && from !== undefined,
    placeholderData: (previous) => previous,
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/accounts/{login}/history/deals', {
        params: { path: { login: login! }, query: { from, limit: 500 } },
      });
      return (data?.items ?? []) as DealLike[];
    },
  }).data;

  const count = bars.length;
  const { up, down } = colors;
  return useMemo(
    () =>
      enabled && deals
        ? {
            markers: tradeMarkers(deals, symbol, bars, offset, { up, down }),
            links: tradeLinks(deals, symbol, bars, offset),
          }
        : { markers: NONE, links: NO_LINKS },
    // Recomputed when bars are added, not on every tick of the live bar (its time does not change).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enabled, deals, symbol, offset, up, down, first, count],
  );
}
